import { apiClient } from './api';
import { UserActionRequest, UserActionResponse, ItemType } from '../types';
import { getElementIdentifier } from './tailwindUtils';

interface LoggerConfig {
  enabled?: boolean;
  batchSize?: number;
  flushInterval?: number;
  debug?: boolean;
  anonymousTracking?: boolean;
}

interface QueuedAction extends UserActionRequest {
  timestamp: number;
}

interface DesktopApi {
  analytics?: {
    logAction: (action: object) => Promise<{ success: boolean }>;
    queueAction: (action: object) => Promise<void>;
    getAppInfo: () => Promise<Record<string, string>>;
  };
}

function getDesktopApi(): DesktopApi | null {
  return typeof window !== 'undefined' && (window as any).api ? (window as any).api : null;
}

export class UserActionLogger {
  private static instance: UserActionLogger;
  private sessionId: string;
  private platform: 'web' | 'desktop';
  private platformInfo: Record<string, string> | null = null;
  private platformInfoReady: Promise<void> | null = null;
  private config: Required<LoggerConfig>;
  private actionQueue: QueuedAction[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  private constructor(config: LoggerConfig = {}) {
    this.config = {
      enabled: true,
      batchSize: 10,
      flushInterval: 5000, // 5 seconds
      debug: import.meta.env.MODE === 'development',
      anonymousTracking: true,
      ...config
    };

    this.platform = this.detectPlatform();
    this.sessionId = this.generateSessionId();
    this.setupBeforeUnloadHandler();
    this.setupVisibilityChangeHandler();
    this.platformInfoReady = this.loadPlatformInfo();

    if (this.config.debug) {
      console.log('[UserActionLogger] Initialized with session:', this.sessionId, 'platform:', this.platform);
    }
  }

  private detectPlatform(): 'web' | 'desktop' {
    return getDesktopApi() ? 'desktop' : 'web';
  }

  private async loadPlatformInfo(): Promise<void> {
    if (this.platform !== 'desktop') return;
    try {
      const info = await getDesktopApi()?.analytics?.getAppInfo();
      if (info) this.platformInfo = info;
    } catch {
      // Silently ignore — platform info is best-effort
    }
  }

  public static getInstance(config?: LoggerConfig): UserActionLogger {
    if (!UserActionLogger.instance) {
      UserActionLogger.instance = new UserActionLogger(config);
    }
    return UserActionLogger.instance;
  }

  private generateSessionId(): string {
    const prefix = this.platform === 'desktop' ? 'desktop_session' : 'session';
    return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupBeforeUnloadHandler(): void {
    window.addEventListener('beforeunload', () => {
      this.flush(true); // Synchronous flush on page unload
    });
  }

  private setupVisibilityChangeHandler(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush(true);
      }
    });
  }

  public async log(actionData: Omit<UserActionRequest, 'session_id'>): Promise<void> {
    if (!this.config.enabled) return;

    // Move any geo_location data into metadata if it exists
    const finalActionData = { itemId: actionData.item_id, ...actionData };
    if ('geo_location' in actionData && (actionData as any).geo_location) {
      finalActionData.metadata = {
        ...finalActionData.metadata,
        geo_location: (actionData as any).geo_location
      };
      delete (finalActionData as any).geo_location;
    }

    // Inject platform metadata into every action
    finalActionData.metadata = {
      ...finalActionData.metadata,
      platform: this.platform,
      ...(this.platformInfo && {
        app_version: this.platformInfo.app_version,
        electron_version: this.platformInfo.electron_version,
        os_platform: this.platformInfo.os_platform,
      }),
    };

    const queuedAction: QueuedAction = {
      ...finalActionData,
      session_id: this.sessionId,
      timestamp: Date.now()
    };

    this.actionQueue.push(queuedAction);

    if (this.config.debug) {
      console.log('[UserActionLogger] Queued action:', queuedAction);
    }

    // Auto-flush if batch size reached
    if (this.actionQueue.length >= this.config.batchSize) {
      await this.flush();
    } else {
      this.scheduleFlush();
    }
  }

  private scheduleFlush(): void {
    if (this.flushTimer) return;

    this.flushTimer = setTimeout(async () => {
      await this.flush();
    }, this.config.flushInterval);
  }

  public async flush(sync: boolean = false): Promise<void> {
    if (this.actionQueue.length === 0) return;

    // Wait for platform info so queued actions can be enriched before sending (with timeout)
    if (this.platformInfoReady) {
      await Promise.race([this.platformInfoReady, new Promise(r => setTimeout(r, 2000))]);
      this.platformInfoReady = null;
    }

    // Backfill platform version info for actions queued before IPC resolved
    if (this.platformInfo) {
      for (const action of this.actionQueue) {
        if (action.metadata && !action.metadata.app_version) {
          action.metadata = {
            ...action.metadata,
            app_version: this.platformInfo.app_version,
            electron_version: this.platformInfo.electron_version,
            os_platform: this.platformInfo.os_platform,
          };
        }
      }
    }

    const actionsToSend = [...this.actionQueue];
    this.actionQueue = [];

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    const desktopApi = getDesktopApi();

    try {
      if (desktopApi?.analytics?.logAction) {
        // Desktop: route through main process IPC (has auth)
        for (const action of actionsToSend) {
          await desktopApi.analytics.logAction(action);
        }
      } else if (sync) {
        // Web: use sendBeacon for synchronous requests during page unload
        for (const action of actionsToSend) {
          this.sendBeacon(action);
        }
      } else {
        // Web: send via httpClient
        for (const action of actionsToSend) {
          await this.sendAction(action);
        }
      }

      if (this.config.debug) {
        console.log(`[UserActionLogger] Flushed ${actionsToSend.length} actions`);
      }
    } catch (error) {
      if (this.config.debug) {
        console.error('[UserActionLogger] Failed to flush actions:', error);
      }

      // Fallback: persist to offline queue (desktop) or re-queue in memory (web)
      if (desktopApi?.analytics?.queueAction) {
        for (const action of actionsToSend) {
          try { await desktopApi.analytics.queueAction(action); } catch { /* best-effort */ }
        }
      } else if (this.actionQueue.length < 50) {
        this.actionQueue.unshift(...actionsToSend);
      }
    }
  }

  private async sendAction(action: QueuedAction): Promise<UserActionResponse> {
    const actionData = action;
    
    try {
      if (typeof actionData.item_id !== 'string') {
        actionData.item_id = (actionData.item_id as any)?.toString();
      }
      const response = await apiClient.logUserAction(actionData);
      return response;
    } catch (error) {
      if (this.config.debug) {
        console.error('[UserActionLogger] Failed to send action:', error);
      }
      throw error;
    }
  }

  private sendBeacon(action: QueuedAction): void {
    const actionData = action;
    
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(actionData)], { type: 'application/json' });
      
      // Determine backend URL based on environment (matching HttpClient logic)
      let backendURL = import.meta.env.VITE_BACKEND_URL;
      if (!backendURL) {
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          backendURL = 'http://localhost:8000/api/v1';
        } else {
          backendURL = '/api/v1';
        }
      }
      
      const url = `${backendURL}/analytics/log-action`;
      
      // Get auth token if available
      const token = localStorage.getItem('supabase.auth.token') || sessionStorage.getItem('supabase.auth.token');
      
      if (token || this.config.anonymousTracking) {
        navigator.sendBeacon(url, blob);
      }
    }
  }

  // Utility methods for common actions
  public async click(
    element: string | HTMLElement,
    metadata?: Record<string, any>
  ): Promise<void> {
    const elementType = typeof element === 'string' ? 'unknown' : element.tagName.toLowerCase();
    const elementId = typeof element === 'string' ? element : getElementIdentifier(element);

    return this.log({
      action_type: 'click',
      item_type: this.inferItemType(elementType),
      item_id: elementId,
      metadata: {
        element_type: elementType,
        ...metadata
      }
    });
  }

  public async pageView(
    page: string, 
    metadata?: Record<string, any>
  ): Promise<void> {
    return this.log({
      action_type: 'view',
      item_type: 'page',
      item_id: page,
      metadata: {
        page_title: document.title,
        url: window.location.href,
        referrer: document.referrer,
        ...metadata
      }
    });
  }

  public async formSubmit(
    formId: string,
    formData?: Record<string, any>,
    success: boolean = true,
    errorMessage?: string
  ): Promise<void> {
    return this.log({
      action_type: 'submit',
      item_type: 'form',
      item_id: formId,
      success,
      error_message: errorMessage,
      request_data: formData ? this.sanitizeFormData(formData) : undefined,
      metadata: {
        form_fields: formData ? Object.keys(formData) : undefined
      }
    });
  }

  public async download(
    fileId: string,
    fileName?: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    return this.log({
      action_type: 'download',
      item_type: 'file',
      item_id: fileId,
      metadata: {
        file_name: fileName,
        ...metadata
      }
    });
  }

  public async search(
    query: string,
    resultCount?: number,
    metadata?: Record<string, any>
  ): Promise<void> {
    return this.log({
      action_type: 'search',
      item_type: 'search_box',
      request_data: {
        query,
        result_count: resultCount
      },
      metadata
    });
  }

  public async error(
    errorType: string,
    errorMessage: string,
    context?: Record<string, any>
  ): Promise<void> {
    return this.log({
      action_type: 'error',
      item_type: 'system',
      success: false,
      error_message: errorMessage,
      metadata: {
        error_type: errorType,
        ...context
      }
    });
  }

  public async navigation(
    from: string,
    to: string,
    method: 'click' | 'browser' | 'redirect' = 'click'
  ): Promise<void> {
    return this.log({
      action_type: 'navigate',
      item_type: 'page',
      item_id: to,
      metadata: {
        from_page: from,
        to_page: to,
        navigation_method: method
      }
    });
  }

  // Performance tracking
  public async trackResponseTime(
    action: string,
    startTime: number,
    metadata?: Record<string, any>
  ): Promise<void> {
    const responseTime = Date.now() - startTime;
    
    return this.log({
      action_type: 'performance',
      item_type: 'api',
      item_id: action,
      response_time: responseTime,
      metadata: {
        ...metadata,
        performance_category: this.categorizeResponseTime(responseTime)
      }
    });
  }

  // Helper methods
  private inferItemType(elementType: string): ItemType {
    const typeMap: Record<string, ItemType> = {
      'button': 'button',
      'input': 'form',
      'form': 'form',
      'a': 'link',
      'table': 'table',
      'select': 'dropdown',
      'div': 'button', // Many custom buttons are divs/span - Revisit
      'span': 'button'
    };
    
    return typeMap[elementType.toLowerCase()] || 'button';
  }

  private sanitizeFormData(formData: Record<string, any>): Record<string, any> {
    const sensitiveFields = ['password', 'token', 'secret', 'key', 'auth'];
    const sanitized: Record<string, any> = {};
    
    Object.keys(formData).forEach(key => {
      if (sensitiveFields.some(field => key.toLowerCase().includes(field))) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = formData[key];
      }
    });
    
    return sanitized;
  }

  private categorizeResponseTime(responseTime: number): string {
    // Placeholders for now, can be updated as needed
    if (responseTime < 200) return 'fast';
    if (responseTime < 1000) return 'medium';
    if (responseTime < 3000) return 'slow';
    return 'very_slow';
  }

  // Configuration methods
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    if (this.config.debug) {
      console.log(`[UserActionLogger] ${enabled ? 'Enabled' : 'Disabled'} logging`);
    }
  }

  public setDebug(debug: boolean): void {
    this.config.debug = debug;
  }

  public getSessionId(): string {
    return this.sessionId;
  }

  public getQueueLength(): number {
    return this.actionQueue.length;
  }

  // Cleanup
  public destroy(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
    
    this.flush(true);
    
    if (this.config.debug) {
      console.log('[UserActionLogger] Logger destroyed');
    }
  }
}

// Create and export singleton instance
export const userActionLogger = UserActionLogger.getInstance();

// Export helper for React components
export const useUserActionLogger = () => userActionLogger;